import React, { useEffect, useRef } from 'react';

const BgCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let width, height, dpr;
    let nodes = [];
    let rings = [];
    let mouse = { x: -9999, y: -9999, active: false };
    let rafId = null;

    const TEAL = '94, 234, 212';
    const AMBER = '240, 178, 84';
    const DOT_COLOR = '231, 236, 239';

    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildNodes();
    }

    function onResize() {
      resize();
      if (prefersReduced) renderStaticFrame();
    }

    function buildNodes() {
      const spacing = 90;
      const cols = Math.ceil(width / spacing) + 1;
      const rows = Math.ceil(height / spacing) + 1;
      nodes = [];
      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const baseX = i * spacing + (j % 2 === 0 ? 0 : spacing / 2);
          const baseY = j * spacing;
          nodes.push({
            baseX, baseY,
            x: baseX, y: baseY,
            vx: 0, vy: 0,
            phase: Math.random() * Math.PI * 2,
            speed: 0.4 + Math.random() * 0.4
          });
        }
      }
    }

    function addRing(x, y, color) {
      rings.push({ x, y, r: 0, maxR: 160, alpha: 0.55, color: color || TEAL });
    }

    function step(t) {
      ctx.clearRect(0, 0, width, height);

      // faint static dot grid as a base layer (keeps the original texture)
      ctx.fillStyle = `rgba(${DOT_COLOR}, 0.045)`;
      const gridSize = 28;
      for (let gx = 0; gx < width; gx += gridSize) {
        for (let gy = 0; gy < height; gy += gridSize) {
          ctx.fillRect(gx, gy, 1, 1);
        }
      }

      // update + draw nodes
      for (const n of nodes) {
        // gentle idle drift
        const driftX = Math.sin(t * 0.0002 * n.speed + n.phase) * 6;
        const driftY = Math.cos(t * 0.00025 * n.speed + n.phase) * 6;
        let targetX = n.baseX + driftX;
        let targetY = n.baseY + driftY;

        // mouse influence: push nodes away within radius
        if (mouse.active) {
          const dx = targetX - mouse.x;
          const dy = targetY - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const radius = 140;
          if (dist < radius && dist > 0.01) {
            const force = (1 - dist / radius) * 26;
            targetX += (dx / dist) * force;
            targetY += (dy / dist) * force;
          }
        }

        n.x += (targetX - n.x) * 0.08;
        n.y += (targetY - n.y) * 0.08;
      }

      // connecting lines between nearby nodes
      ctx.lineWidth = 1;
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let k = i + 1; k < nodes.length; k++) {
          const b = nodes[k];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 95 * 95) {
            const d = Math.sqrt(d2);
            const op = (1 - d / 95) * 0.12;
            if (op > 0.004) {
              ctx.strokeStyle = `rgba(${TEAL}, ${op.toFixed(3)})`;
              ctx.beginPath();
              ctx.moveTo(a.x, a.y);
              ctx.lineTo(b.x, b.y);
              ctx.stroke();
            }
          }
        }
      }

      // dots themselves
      for (const n of nodes) {
        const dx = n.x - mouse.x, dy = n.y - mouse.y;
        const dist = mouse.active ? Math.sqrt(dx * dx + dy * dy) : 9999;
        const near = dist < 140;
        const r = near ? 2.1 : 1.3;
        const color = near ? TEAL : DOT_COLOR;
        const alpha = near ? 0.55 - (dist / 140) * 0.3 : 0.16;
        ctx.fillStyle = `rgba(${color}, ${alpha.toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
        ctx.fill();
      }

      // click ripple rings
      for (let i = rings.length - 1; i >= 0; i--) {
        const ring = rings[i];
        ring.r += 3.2;
        ring.alpha *= 0.965;
        if (ring.r > ring.maxR || ring.alpha < 0.02) {
          rings.splice(i, 1);
          continue;
        }
        ctx.strokeStyle = `rgba(${ring.color}, ${ring.alpha.toFixed(3)})`;
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.arc(ring.x, ring.y, ring.r, 0, Math.PI * 2);
        ctx.stroke();
      }

      rafId = requestAnimationFrame(step);
    }

    function renderStaticFrame() {
      step(0);
      cancelAnimationFrame(rafId);
    }

    function onMouseMove(e) {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      mouse.active = true;
    }
    function onMouseLeave() {
      mouse.active = false;
    }
    function onClick(e) {
      addRing(e.clientX, e.clientY, Math.random() > 0.5 ? TEAL : AMBER);
    }
    function onTouchMove(e) {
      if (e.touches && e.touches[0]) {
        mouse.x = e.touches[0].clientX;
        mouse.y = e.touches[0].clientY;
        mouse.active = true;
      }
    }
    function onTouchStart(e) {
      if (e.touches && e.touches[0]) {
        addRing(e.touches[0].clientX, e.touches[0].clientY, TEAL);
      }
    }

    window.addEventListener('resize', onResize);
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('mouseleave', onMouseLeave, { passive: true });
    window.addEventListener('click', onClick, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });

    resize();

    if (prefersReduced) {
      // Render one static frame, skip continuous animation/movement
      renderStaticFrame();
    } else {
      rafId = requestAnimationFrame(step);
    }

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('click', onClick);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchstart', onTouchStart);
    };
  }, []);

  return <canvas id="bg-canvas" ref={canvasRef} aria-hidden="true" />;
};

export default BgCanvas;
